/* Display-only comparison. Product options, pricing and cart state are never read or written. */
if (!customElements.get('fabric-comparison')) {
  customElements.define('fabric-comparison', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      const groups = [...this.querySelectorAll('[data-fabric-group]')];
      const controls = this.querySelector('[data-fabric-controls]');
      const tabs = this.querySelector('[data-fabric-tabs]');
      const filter = this.querySelector('[data-fabric-differences]');
      if (!groups.length || !controls || !tabs || !filter) return;
      const filterLabel = filter.closest('label');
      const buttons = [];
      // 只在当前可见的组里有同值行时显示筛选；没有标签页也没有筛选时整行控件隐藏。
      const syncControls = () => {
        filterLabel.hidden = !groups.some(group => !group.hidden && group.querySelector('[data-identical]'));
        controls.hidden = tabs.hidden && filterLabel.hidden;
      };
      const updateRows = () => {
        groups.forEach(group => {
          const rows = [...group.querySelectorAll('[data-fabric-row]')];
          rows.forEach(row => { row.hidden = filter.checked && row.hasAttribute('data-identical'); });
          group.querySelector('[data-fabric-empty]').hidden = rows.some(row => !row.hidden);
        });
      };
      const select = index => {
        groups.forEach((group, i) => {
          group.hidden = i !== index;
          buttons[i].setAttribute('aria-selected', String(i === index));
          buttons[i].tabIndex = i === index ? 0 : -1;
        });
        syncControls();
        this.updateOverflow();
      };
      if (groups.length > 1) {
        tabs.hidden = false;
        groups.forEach((group, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.id = group.id + '-tab';
          button.textContent = group.dataset.title;
          button.setAttribute('role', 'tab');
          button.setAttribute('aria-controls', group.id);
          group.setAttribute('role', 'tabpanel');
          group.setAttribute('aria-labelledby', button.id);
          button.addEventListener('click', () => select(index), { signal });
          button.addEventListener('keydown', event => {
            let next = index;
            if (event.key === 'ArrowRight') next = (index + 1) % groups.length;
            else if (event.key === 'ArrowLeft') next = (index + groups.length - 1) % groups.length;
            else if (event.key === 'Home') next = 0;
            else if (event.key === 'End') next = groups.length - 1;
            else return;
            event.preventDefault();
            select(next);
            buttons[next].focus();
          }, { signal });
          tabs.append(button);
          buttons.push(button);
        });
      }
      this.updateOverflow = () => {
        groups.forEach(group => {
          if (group.hidden) return;
          const scroll = group.querySelector('[data-fabric-scroll]');
          const max = scroll.scrollWidth - scroll.clientWidth;
          group.querySelector('[data-fabric-overflow]').hidden = max < 2;
          scroll.toggleAttribute('data-scrolled', scroll.scrollLeft > 1);
          group.querySelector('[data-fabric-prev]').disabled = scroll.scrollLeft <= 1;
          group.querySelector('[data-fabric-next]').disabled = scroll.scrollLeft >= max - 1;
        });
      };
      groups.forEach(group => {
        const scroll = group.querySelector('[data-fabric-scroll]');
        scroll.addEventListener('scroll', this.updateOverflow, { signal, passive: true });
        for (const [selector, direction] of [['[data-fabric-prev]', -1], ['[data-fabric-next]', 1]]) {
          group.querySelector(selector).addEventListener('click', () => {
            const column = group.querySelector('.fabric-compare__fabric');
            const width = column.getBoundingClientRect().width;
            if (!width) return;
            const max = Math.max(0, scroll.scrollWidth - scroll.clientWidth);
            const position = Math.max(0, Math.min(scroll.scrollLeft, max));
            // 末段可能不足一列；返回时对齐上一整列，且不越过实际边界。
            const index = direction > 0 ? Math.floor((position + 1) / width) + 1 : Math.ceil((position - 1) / width) - 1;
            const target = Math.max(0, Math.min(index * width, max));
            const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
            scroll.scrollBy({ left: target - scroll.scrollLeft, behavior: reduce ? 'auto' : 'smooth' });
          }, { signal });
        }
      });
      filter.addEventListener('change', updateRows, { signal });
      window.addEventListener('resize', this.updateOverflow, { signal, passive: true });
      this.addEventListener('load', this.updateOverflow, { signal, capture: true });
      this.addEventListener('shopify:block:select', event => {
        const index = groups.findIndex(group => group === event.target || group.contains(event.target));
        if (index >= 0 && buttons.length) select(index);
      }, { signal });
      updateRows();
      if (buttons.length) select(0);
      else {
        syncControls();
        this.updateOverflow();
      }
      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(this.updateOverflow);
        this.observer.observe(this);
      }
      this.setupGallery(signal);
    }
    // 图片/视频弹窗：只在本页浏览素材和切换对比面料，不跳转、不改商品选项或购物车。
    setupGallery(signal) {
      const dialog = this.querySelector('[data-fabric-dialog]');
      if (!dialog) return;
      const $ = selector => dialog.querySelector(selector);
      const card = $('[data-gallery-card]'), track = $('[data-gallery-track]'), thumbs = $('[data-gallery-thumbs]');
      const fabrics = $('[data-gallery-fabrics]'), filters = $('[data-gallery-filters]'), details = $('[data-gallery-details]');
      const count = $('[data-gallery-count]'), title = $('[data-gallery-name]'), prev = $('[data-gallery-prev]'), next = $('[data-gallery-next]');
      const reduce = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const state = { index: 0, kind: 'all', template: null, trigger: null, scrollY: null };
      let positionFrame = 0;
      const cancelPosition = () => {
        if (positionFrame) window.cancelAnimationFrame?.(positionFrame);
        positionFrame = 0;
        track.style.overflowX = '';
        track.style.scrollSnapType = '';
      };
      const isOpen = () => dialog.hasAttribute('open');
      const visible = list => [...list.children].filter(item => !item.hidden);
      const pauseAll = () => track.querySelectorAll('video').forEach(video => video.pause?.());
      const revealThumb = thumb => {
        if (!thumb) return;
        const box = thumbs.getBoundingClientRect(), rect = thumb.getBoundingClientRect();
        if (rect.left < box.left) thumbs.scrollLeft -= box.left - rect.left + 8;
        else if (rect.right > box.right) thumbs.scrollLeft += rect.right - box.right + 8;
        if (rect.top < box.top) thumbs.scrollTop -= box.top - rect.top + 8;
        else if (rect.bottom > box.bottom) thumbs.scrollTop += rect.bottom - box.bottom + 8;
      };
      const sync = () => {
        const slides = visible(track), list = visible(thumbs);
        count.textContent = slides.length > 1 ? `${state.index + 1} / ${slides.length}` : '';
        // 分类隐藏的媒体也要暂停，不能只同步仍可见的 slide。
        [...track.children].forEach(slide => {
          const active = slide === slides[state.index];
          slide.inert = !active;
          const frame = slide.querySelector('iframe');
          if (frame) {
            if (!frame.dataset.gallerySrc) frame.dataset.gallerySrc = frame.getAttribute('src') || '';
            if (active && !frame.hasAttribute('src')) frame.setAttribute('src', frame.dataset.gallerySrc);
            else if (!active) frame.removeAttribute('src');
          }
          const video = slide.querySelector('video');
          if (!video) return;
          // Safari 可能在 template 克隆的视频上停留于 HAVE_NOTHING；首次显示时明确选取来源。
          if (active && !video.dataset.galleryInitialized) {
            video.dataset.galleryInitialized = 'true';
            video.removeAttribute('loading');
            video.load?.();
          }
          if (active && !reduce()) {
            video.muted = true;
            video.play?.()?.catch?.(() => {});
          } else video.pause?.();
        });
        list.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === state.index)));
        revealThumb(list[state.index]);
        prev.disabled = state.index <= 0;
        next.disabled = state.index >= slides.length - 1;
        prev.hidden = next.hidden = slides.length < 2;
      };
      const go = (index, smooth) => {
        cancelPosition();
        const slides = visible(track);
        if (!slides.length) return;
        state.index = Math.max(0, Math.min(slides.length - 1, index));
        const left = slides[state.index].offsetLeft;
        if (typeof track.scrollTo === 'function') {
          if ((!smooth || reduce()) && window.requestAnimationFrame) {
            // 暂停原生滚动与吸附，让 Safari 也能中断旧动画；下一帧恢复真实横滑。
            track.style.scrollSnapType = 'none';
            track.style.overflowX = 'hidden';
            void track.offsetWidth;
            track.scrollTo({ left, behavior: 'instant' });
            positionFrame = window.requestAnimationFrame(() => {
              cancelPosition();
              if (isOpen()) track.scrollTo({ left, behavior: 'instant' });
            });
          } else track.scrollTo({ left, behavior: smooth && !reduce() ? 'smooth' : 'auto' });
        }
        else track.scrollLeft = left;
        sync();
      };
      const applyKind = kind => {
        state.kind = kind;
        for (const item of [...track.children, ...thumbs.children]) item.hidden = kind !== 'all' && item.dataset.kind !== kind;
        filters.querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.kind === kind)));
        go(0, false);
      };
      const buildFilters = () => {
        const kinds = ['video', 'closeup', 'room'].filter(kind => track.querySelector(`[data-kind="${kind}"]`));
        filters.replaceChildren();
        filters.hidden = kinds.length < 2;
        if (filters.hidden) return;
        for (const kind of ['all', ...kinds]) {
          const button = document.createElement('button');
          const total = kind === 'all' ? track.children.length : track.querySelectorAll(`[data-kind="${kind}"]`).length;
          button.type = 'button';
          button.dataset.kind = kind;
          button.className = 'fabric-gallery__filter';
          button.textContent = `${filters.dataset['label' + kind[0].toUpperCase() + kind.slice(1)] || kind} (${total})`;
          button.addEventListener('click', () => applyKind(kind));
          filters.append(button);
        }
      };
      // 弹窗里顺带列出这款面料在表格里的对比值，看图时不用来回对照。
      const buildDetails = template => {
        details.replaceChildren();
        const cell = template.closest('th');
        const table = cell?.closest('table');
        if (!table) return;
        for (const row of table.querySelectorAll('tbody tr')) {
          const value = row.cells[cell.cellIndex];
          if (!value || value.querySelector('.fabric-compare__missing')) continue;
          const term = document.createElement('dt'), description = document.createElement('dd');
          term.textContent = row.cells[0].textContent.trim();
          description.textContent = value.textContent.trim();
          details.append(term, description);
        }
      };
      const buildFabrics = template => {
        const group = template.closest('[data-fabric-group]');
        const list = [...(group || this).querySelectorAll('template[data-fabric-gallery]')];
        fabrics.replaceChildren();
        fabrics.hidden = list.length < 2;
        for (const item of list) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'fabric-gallery__fabric';
          button.dataset.target = item.id;
          button.setAttribute('aria-pressed', String(item === template));
          if (item.dataset.cover) {
            const image = document.createElement('img');
            image.src = item.dataset.cover;
            image.alt = '';
            image.width = 40;
            image.height = 40;
            button.append(image);
          }
          const label = document.createElement('span');
          label.textContent = item.dataset.name;
          button.append(label);
          if (item.hasAttribute('data-current') && dialog.dataset.currentLabel) {
            const now = document.createElement('span');
            now.className = 'fabric-gallery__now';
            now.textContent = dialog.dataset.currentLabel;
            button.append(now);
          }
          button.addEventListener('click', () => load(item));
          fabrics.append(button);
        }
      };
      const load = template => {
        pauseAll();
        state.template = template;
        const content = template.content.cloneNode(true);
        track.replaceChildren(...content.querySelectorAll('[data-gallery-slide]'));
        thumbs.replaceChildren(...content.querySelectorAll('[data-gallery-thumb]'));
        title.textContent = template.dataset.name;
        fabrics.querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.target === template.id)));
        buildFilters();
        buildDetails(template);
        thumbs.scrollLeft = thumbs.scrollTop = 0;
        applyKind('all');
      };
      const cleanup = () => {
        if (state.scrollY === null) return;
        cancelPosition();
        pauseAll();
        track.replaceChildren();
        thumbs.replaceChildren();
        document.documentElement.classList.remove('fabric-gallery-lock');
        if (Math.abs(window.scrollY - state.scrollY) > 1) window.scrollTo({ top: state.scrollY, left: window.scrollX, behavior: 'instant' });
        state.scrollY = null;
        // 焦点回到打开弹窗的面料图片按钮。
        const trigger = state.trigger;
        if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
        state.trigger = null;
      };
      const close = () => {
        if (isOpen()) {
          if (typeof dialog.close === 'function') dialog.close();
          if (isOpen()) dialog.removeAttribute('open');
        }
        cleanup();
      };
      const open = (id, trigger) => {
        const template = document.getElementById(id);
        if (!template || !this.contains(template)) return;
        state.trigger = trigger;
        if (!isOpen()) {
          state.scrollY = window.scrollY;
          if (typeof dialog.showModal === 'function') dialog.showModal();
          else dialog.setAttribute('open', '');
          document.documentElement.classList.add('fabric-gallery-lock');
        }
        buildFabrics(template);
        load(template);
        // 新一次图片打开从顶部开始；弹窗内切换面料仍保留阅读位置。
        dialog.querySelectorAll('.fabric-gallery__body, .fabric-gallery__aside').forEach(scroller => { scroller.scrollTop = 0; });
        card.focus({ preventScroll: true });
      };
      this.closeGallery = close;
      // 图片按钮在 Liquid 里默认 disabled：没有 JS 时不可聚焦、不可点，脚本就绪后才启用。
      this.querySelectorAll('[data-fabric-open]').forEach(button => { button.disabled = false; });

      this.addEventListener('click', event => {
        const trigger = event.target.closest('[data-fabric-open]');
        if (trigger && this.contains(trigger)) open(trigger.dataset.fabricOpen, trigger);
      }, { signal });
      thumbs.addEventListener('click', event => {
        const thumb = event.target.closest('[data-gallery-thumb]');
        if (thumb) go(visible(thumbs).indexOf(thumb), false);
      }, { signal });
      prev.addEventListener('click', () => go(state.index - 1, true), { signal });
      next.addEventListener('click', () => go(state.index + 1, true), { signal });
      // 手指横滑时按实际停住的那张同步计数、缩略图和视频。
      track.addEventListener('scroll', () => {
        const slides = visible(track);
        if (!slides.length || !track.clientWidth) return;
        const index = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
        if (index !== state.index) {
          state.index = index;
          sync();
        }
      }, { signal, passive: true });
      $('[data-gallery-close]').addEventListener('click', close, { signal });
      dialog.addEventListener('close', cleanup, { signal });
      dialog.addEventListener('cancel', event => {
        event.preventDefault();
        close();
      }, { signal });
      let downOnBackdrop = false;
      dialog.addEventListener('pointerdown', event => { downOnBackdrop = event.target === dialog; }, { signal });
      dialog.addEventListener('click', event => {
        if (downOnBackdrop && event.target === dialog) close();
        downOnBackdrop = false;
      }, { signal });
      dialog.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
          return;
        }
        // 视频控件和面料切换按钮自己处理方向键。
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || event.target.closest('video, [data-gallery-fabrics], [data-gallery-filters]')) return;
        event.preventDefault();
        go(state.index + (event.key === 'ArrowRight' ? 1 : -1), true);
      }, { signal });
    }
    disconnectedCallback() {
      this.closeGallery?.();
      this.closeGallery = null;
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = null;
      this.querySelector('[data-fabric-tabs]')?.replaceChildren();
    }
  });
}
